import { DEFAULT_TIME_ZONE, isKnownZone, zoneLabel } from '@/shared/lib/time-zone';

import { useSelectedCountry } from './use-selected-country';

/**
 * The market time zone to show and take dates and times in.
 *
 * - No argument: the selected market's zone, for the selected market's pages and lists.
 * - A record's `countryId`: that market's zone, for the record's detail page and edit dialog.
 * - `null`, or a market whose zone is missing or unknown to the browser: Palestine time, which
 *   the labels then name.
 *
 * It reads the selected-market store and the current user, so every consumer re-renders when the
 * staff member switches market.
 */
export function useMarketTimeZone(countryId?: number | null): string {
  const { countries, country } = useSelectedCountry();
  const market = countryId === undefined ? country : countries.find((c) => c.id === countryId);
  const zone = market?.ianaTimeZoneId;
  return zone && isKnownZone(zone) ? zone : DEFAULT_TIME_ZONE;
}

/**
 * A record's market zone and, when it differs from the selected market's, the note its page
 * header shows ("Times in Syria time (UTC+03:00)"). Records with no market use Palestine time.
 */
export function useRecordTimeZone(countryId: number | null | undefined): {
  zone: string;
  note: string | null;
} {
  const selected = useMarketTimeZone();
  const zone = useMarketTimeZone(countryId ?? null);
  return { zone, note: zone === selected ? null : `Times in ${zoneLabel(zone)}` };
}
